// /client/src/components/CreateContact.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

// Default confirmation messages
const DEFAULT_OPT_IN_CONFIRMATION = (companyName = 'Next Move') => `You're now subscribed to receiving messages from ${companyName}. Message and data rates may apply. Reply STOP to opt out anytime.`;
const DEFAULT_OPT_OUT_CONFIRMATION = (companyName = 'Next Move') => `You have successfully unsubscribed and will no longer receive messages from ${companyName}. To rejoin at any time, reply START. Message and data rates may apply.`;

// Major US carriers list
const CARRIER_OPTIONS = [
  { value: '', label: 'Select a carrier (optional)' },
  { value: 'att', label: 'AT&T' },
  { value: 'verizon', label: 'Verizon' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'us-cellular', label: 'US Cellular' },
  { value: 'cricket', label: 'Cricket Wireless' },
  { value: 'metro', label: 'Metro by T-Mobile' },
  { value: 'boost', label: 'Boost Mobile' },
  { value: 'visible', label: 'Visible' },
  { value: 'mint', label: 'Mint Mobile' },
  { value: 'google-fi', label: 'Google Fi' },
  { value: 'xfinity', label: 'Xfinity Mobile' },
  { value: 'spectrum', label: 'Spectrum Mobile' },
  { value: 'consumer-cellular', label: 'Consumer Cellular' },
  { value: 'straight-talk', label: 'Straight Talk' },
  { value: 'tracfone', label: 'TracFone' },
  { value: 'simple-mobile', label: 'Simple Mobile' },
  { value: 'other', label: 'Other' }
];

const CreateContact = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    carrier: '',
    role: 'participant',
    status: 'active',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    bio: '',
    emailVerified: false,
    smsVerified: false
  });
  
  const [sendOptinMessage, setSendOptinMessage] = useState(false);
  const [optinMessage, setOptinMessage] = useState('');
  const [customOptInConfirmation, setCustomOptInConfirmation] = useState('');
  const [customOptOutConfirmation, setCustomOptOutConfirmation] = useState('');
  const [showConfirmationMessages, setShowConfirmationMessages] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('Next Move');

  // Generate default opt-in message when firstName changes
  const generateDefaultOptinMessage = (firstName, companyName = 'Next Move') => {
    return `Hello${firstName ? ` ${firstName}` : ''}. This is an automated message from the team at ${companyName}. To receive daily prompts and messages from our program, reply JOIN. We recommend adding ${companyName} to your contacts before typing JOIN.\n\nYou can opt out anytime by replying STOP. Message and data rates may apply. Learn more about our privacy practices at nextmovenetwork.com.`;
  };

  // Update opt-in message when firstName changes and sendOptinMessage is true
  useEffect(() => {
    if (sendOptinMessage && !optinMessage) {
      setOptinMessage(generateDefaultOptinMessage(formData.firstName, companyName));
    }
  }, [formData.firstName, sendOptinMessage]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else if (name === 'phone') {
      // Only allow digits, max 10
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        phone: digitsOnly
      }));
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        };
        
        // Reset verification fields when role changes
        if (name === 'role') {
          if (value === 'participant') {
            // Participants don't need email verification
            newData.emailVerified = false;
            // Clear password fields for participants
            newData.password = '';
            newData.confirmPassword = '';
          } else if (value === 'admin') {
            // Admin don't need SMS verification and no password entry
            newData.smsVerified = false;
            newData.password = '';
            newData.confirmPassword = '';
          } else {
            // Host don't need SMS verification
            newData.smsVerified = false;
          }
        }

        // Update opt-in message when firstName changes
        if (name === 'firstName' && sendOptinMessage) {
          setOptinMessage(generateDefaultOptinMessage(value, companyName));
        }
        
        return newData;
      });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/company', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.company?.name) {
            setCompanyName(data.company.name);
            
          }
        } else {
          
        }
      } catch (error) {
        console.error('CreateContact: Failed to fetch company name:', error);
      }
    };
    fetchCompanyName();
  }, []);

  // Update confirmation messages when companyName loads
  useEffect(() => {
    if (companyName) {
      setCustomOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION(companyName));
      setCustomOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION(companyName));
    }
  }, [companyName]);

  // Handle opt-in checkbox change
  const handleOptinCheckboxChange = (e) => {
    const checked = e.target.checked;
    setSendOptinMessage(checked);
    if (checked && !optinMessage) {
      setOptinMessage(generateDefaultOptinMessage(formData.firstName, companyName));
    }
  };

  // Handle emailVerified checkbox - disable sendWelcomeEmail when checked
  const handleEmailVerifiedChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({ ...prev, emailVerified: checked }));
    if (checked) {
      setSendWelcomeEmail(false);
    }
  };

  // Handle sendWelcomeEmail checkbox - disable emailVerified when checked
  const handleWelcomeEmailChange = (e) => {
    const checked = e.target.checked;
    setSendWelcomeEmail(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, emailVerified: false }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation - required only for Admin and Host
    if (formData.role === 'admin' || formData.role === 'host') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required for Admin and Host roles';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      // For participants, email is optional but must be valid if provided
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation - only required for Host role (not Admin anymore)
    if (formData.role === 'host') {
      if (!formData.password) {
        newErrors.password = 'Password is required for Host role';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters long';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Phone validation - required for participants
    if (formData.role === 'participant') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required for Participants';
      } else if (formData.phone.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      }
    } else if (sendOptinMessage && !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required to send opt-in message';
    } else if (formData.phone && formData.phone.length !== 10 && formData.phone.length > 0) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    // Validate opt-in message if sending
    if (sendOptinMessage && !optinMessage.trim()) {
      newErrors.optinMessage = 'Opt-in message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to format phone to E.164 (matches EditContact.js)
  const formatPhoneE164 = (phone) => {
    if (!phone) return '';
    let formatted = phone.trim().replace(/\D/g, '');
    if (formatted.length === 10) formatted = '1' + formatted;
    if (!formatted.startsWith('+')) formatted = '+' + formatted;
    return formatted;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        setErrors({ submit: 'Authentication token not found. Please log in again.' });
        return;
      }
      
      // Prepare data for submission
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      
      // Format phone number with +1 prefix if provided
      if (submitData.phone) {
        submitData.phone = '+1' + submitData.phone;
      }
      
      // Remove password field if role is participant or admin
      if (submitData.role === 'participant' || submitData.role === 'admin') {
        delete submitData.password;
      }
      
      // Ensure correct verification based on role
      if (submitData.role === 'participant') {
        submitData.emailVerified = false;
      } else {
        // Admin or Host
        submitData.smsVerified = false;
      }
      
      // Don't send opt-in flags to backend - we'll handle it directly here
      submitData.sendOptinMessage = false;
      submitData.optinMessage = null;
      submitData.sendWelcomeEmail = sendWelcomeEmail;
      
      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(submitData)
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        let successMessage = `Contact ${formData.firstName} ${formData.lastName} created successfully!`;
        
        // Send opt-in message separately via Twilio endpoint (like EditContact does)
        if (sendOptinMessage && formData.phone && !formData.smsVerified) {
          try {
            // Use the same phone formatting as EditContact.js
            const formattedPhone = formatPhoneE164(formData.phone);
            
            const optinResponse = await fetch('/api/twilio/send-optin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                phoneNumber: formattedPhone,
                firstName: formData.firstName,
                lastName: formData.lastName,
                contactId: responseData.contact?.id,
                customMessage: optinMessage || null,
                customOptInConfirmation: customOptInConfirmation || null,
                customOptOutConfirmation: customOptOutConfirmation || null
              })
            });

            const optinResponseData = await optinResponse.json();            
            
            if (optinResponse.ok) {
              successMessage += ' Opt-in message has been scheduled.';
            } else {
              console.error('Failed to send opt-in message:', optinResponseData);
              successMessage += ` (Opt-in failed: ${optinResponseData.error || optinResponseData.message || 'Unknown error'})`;
            }
          } catch (optinError) {
            console.error('Error sending opt-in message:', optinError);
            successMessage += ` (Opt-in error: ${optinError.message})`;
          }
        }
        
        if (sendWelcomeEmail && responseData.welcomeEmailSent) {
          successMessage += ' Welcome email has been sent.';
        }
        
        navigate('/contacts', {
          state: { successMessage }
        });
      } else {
        console.error('Failed to create contact:', responseData);
        setErrors({ 
          submit: responseData.error || 'Failed to create contact. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      setErrors({ 
        submit: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine if password fields should be shown (only for Host now)
  const showPasswordFields = formData.role === 'host';
  
  // Determine if email is required based on role
  const emailRequired = formData.role === 'admin' || formData.role === 'host';
  
  // Determine if phone is required based on role
  const phoneRequired = formData.role === 'participant';

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
            <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            <div className='is-flex is-flex-direction-rows is-justify-content-space-between is-align-items-center'>
                <h1 className='h1 my-2'>Create Contact</h1>
            </div>
          </div>

          <div className='container px-6'>
            <form onSubmit={handleSubmit} className="boxo">
              
              {/* General Error Message */}
              {errors.submit && (
                <div className="notification is-danger">
                  <button 
                    className="delete" 
                    onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}
                  ></button>
                  {errors.submit}
                </div>
              )}

              <div className="columns">
                <div className="column is-half">
                  {/* First Name - Required */}
                  <div className="field">
                    <label className="label">First Name *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.firstName ? 'is-danger' : ''}`}
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter first name"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="help is-danger">{errors.firstName}</p>
                    )}
                  </div>

                  {/* Last Name - Required */}
                  <div className="field">
                    <label className="label">Last Name *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.lastName ? 'is-danger' : ''}`}
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter last name"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="help is-danger">{errors.lastName}</p>
                    )}
                  </div>
                  
                  {/* Email - Required for Admin/Host, Optional for Participant */}
                  <div className="field">
                    <label className="label">
                      Email Address {emailRequired ? '*' : '(Optional)'}
                    </label>
                    <div className="control">
                      <input 
                        className={`input ${errors.email ? 'is-danger' : ''}`}
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                      />
                    </div>
                    {errors.email && (
                      <p className="help is-danger">{errors.email}</p>
                    )}
                    {!emailRequired && (
                      <p className="mx-2 mt-1 is-size-7">Email is optional for Participants</p>
                    )}
                  </div>

                  {/* Password - Only for Host */}
                  {showPasswordFields && (
                    <>
                      <div className="field">
                        <label className="label">Password *</label>
                        <div className="control">
                          <input 
                            className={`input ${errors.password ? 'is-danger' : ''}`}
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Enter password"
                          />
                        </div>
                        {errors.password && (
                          <p className="help is-danger">{errors.password}</p>
                        )}
                      </div>

                      <div className="field">
                        <label className="label">Confirm Password *</label>
                        <div className="control">
                          <input 
                            className={`input ${errors.confirmPassword ? 'is-danger' : ''}`}
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder="Confirm password"
                          />
                        </div>
                        {errors.confirmPassword && (
                          <p className="help is-danger">{errors.confirmPassword}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Phone - Required for Participant, Optional for others */}
                  <div className="field">
                    <label className="label">
                      Phone Number {phoneRequired ? '*' : (sendOptinMessage ? '*' : '')}
                    </label>
                    <div className="control has-icons-left">
                      <input 
                        className={`input ${errors.phone ? 'is-danger' : ''}`}
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="1234567890"
                        maxLength="10"
                        style={{ paddingLeft: '45px' }}
                      />
                      <span className="icon is-small is-left" style={{ pointerEvents: 'none', width: '45px' }}>
                        <span style={{ fontSize: '14px', color: '#4a4a4a' }}>+1</span>
                      </span>
                    </div>
                    {errors.phone && (
                      <p className="help is-danger">{errors.phone}</p>
                    )}
                    <p className="mx-2 mt-1 is-size-7">Enter 10-digit US phone number (without +1)</p>
                  </div>

                  {/* Carrier - Optional */}
                  <div className="field">
                    <label className="label">Carrier</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select 
                          name="carrier"
                          value={formData.carrier}
                          onChange={handleInputChange}
                        >
                          {CARRIER_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="mx-2 mt-1 is-size-7">Mobile carrier (optional)</p>
                  </div>

                  {/* Send Opt-in Message Checkbox - Only for Participants */}
                  {formData.role === 'participant' && (
                    <div className="mt-0 pt-5 field">
                      <div className="box" style={{ backgroundColor: '#f5f5f5' }}>
                        <label className="checkbox">
                          <input 
                            type="checkbox"
                            checked={sendOptinMessage}
                            onChange={handleOptinCheckboxChange}
                            disabled={!formData.phone || formData.smsVerified}
                          />
                          <span className="ml-2">
                            Send Opt-in Message via SMS
                          </span>
                        </label>
                        <p className="mx-5 is-size-7 mt-2">
                          {formData.smsVerified 
                            ? 'User is already opted in'
                            : !formData.phone
                            ? 'Phone number required to send opt-in message'
                            : 'User will receive opt-in request'
                          }
                        </p>

                        {/* Opt-in Message Textarea - Shows when checkbox is checked */}
                        {sendOptinMessage && formData.phone && !formData.smsVerified && (
                          <div className="mt-4">
                            <div className="field">
                              <label className="label is-small">Opt-in Request Message</label>
                              <div className="control">
                                <textarea
                                  className={`textarea ${errors.optinMessage ? 'is-danger' : ''}`}
                                  value={optinMessage}
                                  onChange={(e) => setOptinMessage(e.target.value)}
                                  rows="5"
                                />
                              </div>
                              {errors.optinMessage && (
                                <p className="help is-danger">{errors.optinMessage}</p>
                              )}
                              <p className="mx-2 mt-1 is-size-7">{optinMessage.length} characters</p>
                            </div>

                            {/* Toggle for confirmation messages */}
                            <div className="field mt-4">
                              <button
                                type="button"
                                className="button is-small is-light"
                                onClick={() => setShowConfirmationMessages(!showConfirmationMessages)}
                              >
                                {showConfirmationMessages ? '▼' : '▶'} Customize Auto-Response Messages
                              </button>
                              <p className="label is-small">
                                Customize the messages sent when user replies JOIN or STOP
                              </p>
                            </div>

                            {/* Custom Confirmation Messages */}
                            {showConfirmationMessages && (
                              <div className="mt-3 p-3" style={{ backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #dbdbdb' }}>
                                {/* Opt-In Confirmation */}
                                <div className="field">
                                  <label className="label is-small">
                                    Opt-In Confirmation Message
                                    <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies JOIN)</span>
                                  </label>
                                  <div className="control">
                                    <textarea
                                      className="textarea is-small"
                                      value={customOptInConfirmation}
                                      onChange={(e) => setCustomOptInConfirmation(e.target.value)}
                                      rows="3"
                                    />
                                  </div>
                                  <p className="mx-2 mt-1 is-size-7">
                                    {customOptInConfirmation === DEFAULT_OPT_IN_CONFIRMATION 
                                      ? `${customOptInConfirmation.length} characters (default)` 
                                      : `${customOptInConfirmation.length} characters (customized)`}
                                  </p>
                                </div>

                                {/* Opt-Out Confirmation */}
                                <div className="field mt-3">
                                  <label className="label is-small">
                                    Opt-Out Confirmation Message
                                    <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies STOP)</span>
                                  </label>
                                  <div className="control">
                                    <textarea
                                      className="textarea is-small"
                                      value={customOptOutConfirmation}
                                      onChange={(e) => setCustomOptOutConfirmation(e.target.value)}
                                      rows="3"
                                    />
                                  </div>
                                  <p className="mx-2 mt-1 is-size-7">
                                    {customOptOutConfirmation === DEFAULT_OPT_OUT_CONFIRMATION 
                                      ? `${customOptOutConfirmation.length} characters (default)` 
                                      : `${customOptOutConfirmation.length} characters (customized)`}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
                
                <div className="column is-half">
                  
                  {/* Role */}
                  <div className="field">
                    <label className="label">Role</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select 
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                        >
                          <option value="participant">Participant</option>
                          <option value="host">Host</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="field">
                    <label className="label">Status</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select 
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="active">Active</option>
                          {/* <option value="inactive">Inactive</option> */}
                          {/* <option value="suspended">Suspended</option> */}
                          <option value="archived">Archived</option>
                          <option value="prospecting">Prospecting</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Address Fields */}
                  <div className="field">
                    <label className="label">Street Address</label>
                    <div className="control">
                      <input 
                        className="input"
                        type="text"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleInputChange}
                        placeholder="Enter street address (optional)"
                      />
                    </div>
                  </div>

                  <div className="columns is-gapless">
                    <div className="column">
                      <div className="field">
                        <label className="my-2 p-0 label">City</label>
                        <div className="control">
                          <input 
                            className="input"
                            type="text"
                            name="address.city"
                            value={formData.address.city}
                            onChange={handleInputChange}
                            placeholder="City"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column is-narrow">
                      <div className="field ml-2">
                        <label className="my-2 p-0 label">State</label>
                        <div className="control">
                          <input 
                            className="input"
                            type="text"
                            name="address.state"
                            value={formData.address.state}
                            onChange={handleInputChange}
                            placeholder="State"
                            style={{ width: '80px' }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column is-narrow">
                      <div className="field ml-2">
                        <label className="my-2 p-0 label">Zip</label>
                        <div className="control">
                          <input 
                            className="input"
                            type="text"
                            name="address.zipCode"
                            value={formData.address.zipCode}
                            onChange={handleInputChange}
                            placeholder="Zip"
                            style={{ width: '90px' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="field">
                    <label className="label mt-0 pt-0">Bio/Notes</label>
                    <div className="control">
                      <textarea 
                        className="textarea"
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        placeholder="Enter notes or bio (optional)"
                        rows="3"
                      />
                    </div>
                  </div>

                  {/* Email Verified & Welcome Email - Only for Admin and Host */}
                  {(formData.role === 'admin' || formData.role === 'host') && (
                    <>
                      <div className="mx-2 field">
                        <div className="control">
                          <label className="checkbox">
                            <input 
                              type="checkbox"
                              name="emailVerified"
                              checked={formData.emailVerified}
                              onChange={handleEmailVerifiedChange}
                              disabled={sendWelcomeEmail}
                            />
                            <span className="ml-2">Mark email as verified to login.</span>
                          </label>
                        </div>
                      </div>

                      {/* Send Welcome Email - Only for Admin and Host */}
                      {(formData.role === 'admin' || formData.role === 'host') && (
                        <div className="mx-2 field">
                          <div className="control">
                            <label className="checkbox">
                              <input 
                                type="checkbox"
                                checked={sendWelcomeEmail}
                                onChange={handleWelcomeEmailChange}
                                disabled={formData.emailVerified}
                              />
                              <span className="ml-2">Send user a welcome email.</span>
                            </label>
                          </div>
                          <p className="mx-5 is-size-7 mt-2">
                            {formData.emailVerified
                              ? 'Disabled when email is already verified.'
                              : 'User will receive an email with a verification link before password setup.'}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* SMS Verified - Only for Participants */}
                  {formData.role === 'participant' && (
                    <div className="mx-2 field">
                      <div className="control">
                        <label className="checkbox">
                          <input 
                            type="checkbox"
                            name="smsVerified"
                            checked={formData.smsVerified}
                            onChange={handleInputChange}
                          />
                          <span className="ml-2">Mark phone as verified to receive text messages.</span>
                        </label>
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Submit Button */}
              <div className="field is-grouped mt-0 pt-0 pb-6">
                <div className="control">
                  <button 
                    className={`button is-primary ${loading ? 'is-loading' : ''}`}
                    type="submit"
                    disabled={loading}
                  >
                    Create New Contact
                  </button>
                </div>
                <div className="control">
                  <button 
                    className="button is-light"
                    type="button"
                    onClick={() => navigate('/contacts')}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>
      </section>
    </>
  );
};

export default CreateContact;